import React, { useState, useMemo, useEffect } from "react";
import Papa from "papaparse";
import {
  Upload,
  Briefcase,
  Check,
  Filter,
  Search,
  Flame,
  Snowflake,
  Sun,
  ExternalLink,
  Activity,
  ArrowUpRight,
  Zap,
  Users,
  Layers,
} from "lucide-react";
import _ from "lodash";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// --- CONFIGURATION ---
// ⚠️ ENSURE THIS FILE IS IN YOUR PUBLIC FOLDER
const PRELOAD_FILENAME = "seon_segmented_signals_trial.csv";

// --- CONSTANTS ---
// We prioritize these specific signals for the "Hot" tier
const HIGH_INTENT_SIGNALS = [
  "Buying intent / evaluation",
  "Pain / problem",
  "Tool comparison",
  "Pricing & commercial",
];

const WARM_SIGNALS = [
  "Hiring / jobs",
  "Partnership / integration",
  "Product feedback / review",
  "Growth",
];

// --- Utility Functions ---
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// --- LocalStorage Helpers ---
const LS_PLAYBOOK = "MARKET_SIGNAL_PLAYBOOK_V1";

function getLocal(k) {
  try {
    return localStorage.getItem(k) || "";
  } catch {
    return "";
  }
}
function setLocal(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch {}
}

// --- UI Components ---
const Card = ({ children, className = "" }) => (
  <div
    className={cn(
      "bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-xl shadow-2xl overflow-hidden",
      className
    )}
  >
    {children}
  </div>
);

const Badge = ({ children, type = "default", className }) => {
  const styles = {
    default: "bg-slate-800 text-slate-300 border-slate-700",
    segment:
      "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]",
    theme: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
    firmo: "bg-blue-500/10 text-blue-300 border-blue-500/20",
    signal: "bg-slate-700 text-slate-300 border-slate-600",
    hot: "bg-red-500/10 text-red-400 border-red-500/20 font-bold shadow-[0_0_10px_rgba(239,68,68,0.15)] flex items-center gap-1",
    warm: "bg-orange-500/10 text-orange-400 border-orange-500/20 flex items-center gap-1",
    cold: "bg-slate-800 text-slate-500 border-slate-700 flex items-center gap-1",
  };
  return (
    <span
      className={cn(
        "px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border inline-block mr-1 mb-1 transition-all hover:scale-105 cursor-default",
        styles[type] || styles.default,
        className
      )}
    >
      {children}
    </span>
  );
};

export default function App() {
  const [data, setData] = useState([]);
  const [playbook, setPlaybook] = useState(() => {
    try {
      return JSON.parse(getLocal(LS_PLAYBOOK)) || [];
    } catch {
      return [];
    }
  });

  // --- FILTER STATES ---
  const [filters, setFilters] = useState({
    tier: "All",
    segment: "All",
    theme: "All",
    search: "",
  });

  // --- CORE LOGIC: PROCESS RAW DATA ---
  const processRawData = (rawData) => {
    const processed = rawData.map((row, index) => {
      // 1. Parse Pipe-Separated Lists from the CSV
      const splitList = (str) => (str ? str.split(" | ").filter(Boolean) : []);

      const segments = row.PrimarySegment
        ? [row.PrimarySegment]
        : splitList(row.SegmentsMatched);

      const themes = splitList(row.Themes);
      const signals = splitList(row.SignalTypes);
      const firmos = splitList(row.FirmographicFitSegments);

      // 2. Auto-Calculate Tier based on Signal Types
      let tier = "Cold";

      // Check for High Intent matches
      const hasHotSignal = signals.some((s) =>
        HIGH_INTENT_SIGNALS.some((h) => s.includes(h) || h.includes(s))
      );
      const hasWarmSignal = signals.some((s) =>
        WARM_SIGNALS.some((w) => s.includes(w) || w.includes(s))
      );

      if (hasHotSignal) tier = "Hot";
      else if (hasWarmSignal) tier = "Warm";

      // 3. Determine Display Label
      let signalLabel = "General Monitor";
      if (tier === "Hot") signalLabel = "High Intent / Risk";
      else if (tier === "Warm") signalLabel = "Market Activity";

      return {
        ...row,
        id: index,
        segments,
        themes,
        signals,
        firmos,
        tier,
        signalLabel,
        // Standardize headers
        Title: row.Title || "No Title",
        Snippet: row.Snippet || "",
        PostURL: row.PostURL || row.link || "#",
        Platform: row.Platform || "Web",
        Date: row.PostDate || "",
        Author: row.Author || "",
      };
    });

    // Sort: Hot > Warm > Cold
    const tierOrder = { Hot: 3, Warm: 2, Cold: 1 };
    processed.sort((a, b) => tierOrder[b.tier] - tierOrder[a.tier]);

    setData(processed);
  };

  // --- AUTO-LOAD EFFECT ---
  useEffect(() => {
    // This looks for the file in the public/ folder
    fetch(PRELOAD_FILENAME)
      .then((response) => {
        if (!response.ok) throw new Error("File not found");
        return response.text();
      })
      .then((csvText) => {
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            console.log("Auto-loaded data:", results.data.length);
            processRawData(results.data);
          },
        });
      })
      .catch((err) => {
        console.log("No auto-load file found. Waiting for user upload.");
      });
  }, []);

  // --- MANUAL UPLOAD HANDLER ---
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => processRawData(results.data),
    });
  };

  // --- FILTER LOGIC ---
  const { filteredData, uniqueSegments, uniqueThemes, counts } = useMemo(() => {
    let result = data;

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (d) =>
          d.Title.toLowerCase().includes(q) ||
          d.Snippet.toLowerCase().includes(q)
      );
    }

    if (filters.tier !== "All")
      result = result.filter((d) => d.tier === filters.tier);

    if (filters.segment !== "All")
      result = result.filter((d) => d.segments.includes(filters.segment));

    if (filters.theme !== "All")
      result = result.filter((d) => d.themes.includes(filters.theme));

    const allSegments = _.uniq(data.flatMap((d) => d.segments)).sort();
    const allThemes = _.uniq(data.flatMap((d) => d.themes)).sort();

    const counts = {
      hot: data.filter((d) => d.tier === "Hot").length,
      warm: data.filter((d) => d.tier === "Warm").length,
      cold: data.filter((d) => d.tier === "Cold").length,
      total: data.length,
    };

    return {
      filteredData: result,
      uniqueSegments: allSegments,
      uniqueThemes: allThemes,
      counts,
    };
  }, [data, filters]);

  const togglePlaybook = (item) => {
    const exists = playbook.find((p) => p.id === item.id);
    const newPb = exists
      ? playbook.filter((p) => p.id !== item.id)
      : [...playbook, item];
    setPlaybook(newPb);
    setLocal(LS_PLAYBOOK, JSON.stringify(newPb));
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-emerald-500/30 pb-20">
      {/* --- HEADER --- */}
      <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800 px-6 py-4 flex items-center justify-between shadow-2xl shadow-black/50">
        <div className="flex items-center gap-3">
          {/* Logo / Title Area */}
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight flex flex-col leading-tight">
              Fairway Digital Media
              <span className="text-slate-500 font-light text-sm">
                Social Signal Listening
              </span>
            </h1>
          </div>
        </div>

        <label className="group flex items-center gap-2 px-4 py-2 bg-slate-900 border border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800 text-slate-300 rounded-lg cursor-pointer text-sm font-medium transition-all shadow-lg">
          <Upload className="w-4 h-4 text-emerald-500 group-hover:scale-110 transition-transform" />
          <span>Upload CSV</span>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
      </header>

      <main className="max-w-[1800px] mx-auto p-6 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {/* --- 1. HUD / STATS --- */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-5 flex flex-col justify-between hover:border-slate-600 transition-colors">
            <div className="text-slate-500 text-xs uppercase font-bold tracking-widest mb-1">
              Total Signals
            </div>
            <div className="flex items-end justify-between">
              <div className="text-4xl font-bold text-white">
                {counts.total}
              </div>
              <Activity className="w-6 h-6 text-slate-700" />
            </div>
          </Card>

          <Card className="p-5 flex flex-col justify-between border-red-500/20 bg-red-950/10 hover:bg-red-950/20 transition-all group">
            <div className="text-red-400 text-xs uppercase font-bold tracking-widest mb-1 flex items-center gap-2">
              <Flame className="w-3 h-3" /> High Intent
            </div>
            <div className="flex items-end justify-between">
              <div className="text-4xl font-bold text-white group-hover:text-red-100 transition-colors">
                {counts.hot}
              </div>
              <ArrowUpRight className="w-6 h-6 text-red-500/50 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          </Card>

          <Card className="p-5 flex flex-col justify-between border-orange-500/20 bg-orange-950/10 hover:bg-orange-950/20 transition-all group">
            <div className="text-orange-400 text-xs uppercase font-bold tracking-widest mb-1 flex items-center gap-2">
              <Sun className="w-3 h-3" /> Warm Signals
            </div>
            <div className="flex items-end justify-between">
              <div className="text-4xl font-bold text-white group-hover:text-orange-100 transition-colors">
                {counts.warm}
              </div>
              <Zap className="w-6 h-6 text-orange-500/50" />
            </div>
          </Card>

          <Card className="p-5 flex flex-col justify-between border-slate-700/50 bg-slate-900/30">
            <div className="text-slate-400 text-xs uppercase font-bold tracking-widest mb-1 flex items-center gap-2">
              <Snowflake className="w-3 h-3" /> General Context
            </div>
            <div className="flex items-end justify-between">
              <div className="text-4xl font-bold text-slate-300">
                {counts.cold}
              </div>
            </div>
          </Card>
        </div>

        {/* --- 2. CONTROLS BAR --- */}
        <div className="sticky top-20 z-40">
          <Card className="p-1.5 flex flex-wrap gap-2 items-center shadow-2xl shadow-black/50 bg-slate-900/90">
            <div className="px-3 flex items-center gap-2 text-sm font-medium text-emerald-400 border-r border-slate-700 mr-1">
              <Filter className="w-4 h-4" /> Filter
            </div>

            <select
              className="bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all cursor-pointer"
              value={filters.tier}
              onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
            >
              <option value="All">All Tiers</option>
              <option value="Hot">🔥 Hot Only</option>
              <option value="Warm">☀️ Warm Only</option>
              <option value="Cold">❄️ Cold Only</option>
            </select>

            <select
              className="bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500/50 outline-none max-w-[200px] cursor-pointer"
              value={filters.segment}
              onChange={(e) =>
                setFilters({ ...filters, segment: e.target.value })
              }
            >
              <option value="All">All Segments</option>
              {uniqueSegments.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              className="bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500/50 outline-none max-w-[200px] cursor-pointer"
              value={filters.theme}
              onChange={(e) =>
                setFilters({ ...filters, theme: e.target.value })
              }
            >
              <option value="All">All Themes</option>
              {uniqueThemes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>

            <div className="flex-1 min-w-[250px] relative group">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5 group-focus-within:text-emerald-500 transition-colors" />
              <input
                type="text"
                placeholder="Search signals, snippets..."
                className="w-full bg-slate-950 border border-slate-700 text-slate-200 text-sm rounded-lg pl-9 pr-3 py-2 focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>
          </Card>
        </div>

        {/* --- 3. RICH DATA TABLE --- */}
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[50vh] border-2 border-dashed border-slate-800 rounded-3xl bg-slate-900/20">
            <div className="p-4 bg-slate-800 rounded-full mb-4 animate-bounce">
              <Upload className="w-8 h-8 text-slate-500" />
            </div>
            <p className="text-lg font-medium text-slate-300">
              Awaiting Signal Data...
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Attempting to auto-load "{PRELOAD_FILENAME}"
            </p>
          </div>
        ) : (
          <Card className="overflow-hidden border-0 bg-transparent shadow-none">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-separate border-spacing-y-2">
                <thead className="text-xs uppercase font-bold text-slate-500 tracking-wider">
                  <tr>
                    <th className="px-4 py-2 w-36">Intent Tier</th>
                    <th className="px-4 py-2 w-1/4">Segment & Context</th>
                    <th className="px-4 py-2">Signal Detail</th>
                    <th className="px-4 py-2 w-32 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="p-8 text-center text-slate-500 italic bg-slate-900/50 rounded-xl"
                      >
                        No results found for current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredData.slice(0, 100).map((row) => (
                      <tr
                        key={row.id}
                        className="group transition-all hover:-translate-y-0.5"
                      >
                        {/* TIER COLUMN */}
                        <td className="p-0">
                          <div
                            className={cn(
                              "h-full p-4 rounded-l-xl border-y border-l flex flex-col gap-2 items-start",
                              row.tier === "Hot"
                                ? "bg-red-950/10 border-red-500/20"
                                : row.tier === "Warm"
                                ? "bg-orange-950/10 border-orange-500/20"
                                : "bg-slate-900/80 border-slate-700"
                            )}
                          >
                            {row.tier === "Hot" && (
                              <Badge type="hot">Hot</Badge>
                            )}
                            {row.tier === "Warm" && (
                              <Badge type="warm">Warm</Badge>
                            )}
                            {row.tier === "Cold" && (
                              <Badge type="cold">Cold</Badge>
                            )}

                            <div className="mt-1 text-[10px] font-mono text-slate-500 uppercase leading-tight">
                              {row.signalLabel}
                            </div>
                          </div>
                        </td>

                        {/* METADATA COLUMN */}
                        <td className="p-0">
                          <div
                            className={cn(
                              "h-full p-4 border-y flex flex-col justify-center gap-3",
                              row.tier === "Hot"
                                ? "bg-red-950/5 border-red-500/20"
                                : row.tier === "Warm"
                                ? "bg-orange-950/5 border-orange-500/20"
                                : "bg-slate-900/80 border-slate-700"
                            )}
                          >
                            {/* Segments */}
                            {row.segments.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wider flex items-center gap-1">
                                  <Briefcase className="w-3 h-3" /> Target
                                  Segment
                                </div>
                                <div className="flex flex-wrap gap-y-1">
                                  {row.segments.map((c) => (
                                    <Badge key={c} type="segment">
                                      {c}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Themes */}
                            {row.themes.length > 0 && (
                              <div>
                                <div className="text-[10px] uppercase text-slate-500 font-bold mb-1 tracking-wider flex items-center gap-1">
                                  <Layers className="w-3 h-3" /> Themes
                                </div>
                                <div className="flex flex-wrap gap-y-1">
                                  {row.themes.map((t) => (
                                    <Badge key={t} type="theme">
                                      {t}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Firmographics (if avail) */}
                            {row.firmos.length > 0 && (
                              <div className="pt-1 border-t border-slate-800/50">
                                <div className="flex flex-wrap gap-y-1">
                                  {row.firmos.map((f) => (
                                    <Badge key={f} type="firmo">
                                      {f}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* CONTENT COLUMN */}
                        <td className="p-0">
                          <div
                            className={cn(
                              "h-full p-4 border-y flex flex-col justify-center",
                              row.tier === "Hot"
                                ? "bg-red-950/5 border-red-500/20"
                                : row.tier === "Warm"
                                ? "bg-orange-950/5 border-orange-500/20"
                                : "bg-slate-900/80 border-slate-700"
                            )}
                          >
                            <a
                              href={row.PostURL}
                              target="_blank"
                              rel="noreferrer"
                              className="text-base font-semibold text-slate-200 hover:text-emerald-400 hover:underline mb-1 line-clamp-1 block"
                            >
                              {row.Title}
                            </a>
                            <p className="text-slate-400 text-sm leading-relaxed line-clamp-2 mb-3">
                              {row.Snippet}
                            </p>

                            {/* Signal Tags */}
                            <div className="flex flex-wrap gap-1 mb-2">
                              {row.signals.slice(0, 3).map((sig) => (
                                <span
                                  key={sig}
                                  className="text-[10px] text-slate-400 bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700"
                                >
                                  {sig}
                                </span>
                              ))}
                            </div>

                            <div className="flex items-center gap-3 text-xs text-slate-600 font-mono mt-auto">
                              <span className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-400 border border-slate-700">
                                {row.Platform}
                              </span>
                              {row.Author && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" /> {row.Author}
                                  </span>
                                </>
                              )}
                              {row.Date && (
                                <>
                                  <span>•</span>
                                  <span>{row.Date}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* ACTION COLUMN */}
                        <td className="p-0">
                          <div
                            className={cn(
                              "h-full p-4 rounded-r-xl border-y border-r flex flex-col items-end justify-center gap-2",
                              row.tier === "Hot"
                                ? "bg-red-950/10 border-red-500/20"
                                : row.tier === "Warm"
                                ? "bg-orange-950/10 border-orange-500/20"
                                : "bg-slate-900/80 border-slate-700"
                            )}
                          >
                            <button
                              onClick={() => togglePlaybook(row)}
                              className={cn(
                                "w-full px-3 py-2 rounded-lg text-xs font-bold transition-all border flex items-center justify-center gap-2 shadow-lg",
                                playbook.find((p) => p.id === row.id)
                                  ? "bg-emerald-600 border-emerald-500 text-white shadow-emerald-900/20"
                                  : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
                              )}
                            >
                              {playbook.find((p) => p.id === row.id) ? (
                                <>
                                  Saved <Check className="w-3 h-3" />
                                </>
                              ) : (
                                "Track"
                              )}
                            </button>

                            <a
                              href={row.PostURL}
                              target="_blank"
                              rel="noreferrer"
                              className="w-full px-3 py-2 bg-transparent border border-slate-700/50 hover:border-slate-500 text-slate-400 hover:text-white rounded-lg text-xs flex items-center justify-center gap-2 transition-colors"
                            >
                              View <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
